import { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  Stack,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
  Grid,
  useTheme,
  useMediaQuery,
  Collapse,
} from '@mui/material'
import {
  Add,
  Search,
  MoreVert,
  Edit,
  Delete,
  TrendingUp,
  TrendingDown,
  FilterList,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material'
import { format } from 'date-fns'

import { Transaction, TransactionFormData, Category, Tag, Group } from '../../types'
import { transactionService } from '../../services/transactionService'
import { categoryService } from '../../services/categoryService'
import { tagService } from '../../services/tagService'
import { groupService } from '../../services/groupService'
import LoadingSpinner from '../../components/Common/LoadingSpinner'
import ErrorMessage from '../../components/Common/ErrorMessage'
import ConfirmDialog from '../../components/Common/ConfirmDialog'
import DateRangePicker from '../../components/Common/DateRangePicker'
import { TransactionList } from '../../components/Transactions'
import { useAuth } from '../../contexts/AuthContext'

const TransactionsPage = () => {
  const { user } = useAuth()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [totalCount, setTotalCount] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedType, setSelectedType] = useState<'all' | 'income' | 'expense'>('all')
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null)
  const [selectedGroup, setSelectedGroup] = useState<number | null>(null)
  const [dateRange, setDateRange] = useState<{ start: Date | null; end: Date | null }>({
    start: null,
    end: null,
  })

  // Form state
  const [formOpen, setFormOpen] = useState(false)
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null)
  const [selectedGroupId, setSelectedGroupId] = useState<number | undefined>(undefined)
  const [formData, setFormData] = useState<TransactionFormData>({
    amount: 0,
    description: '',
    type: 'expense',
    categoryId: undefined,
    tagIds: [],
    transactionDate: format(new Date(), 'yyyy-MM-dd'),
  })

  // Data for form
  const [categories, setCategories] = useState<Category[]>([])
  const [tags, setTags] = useState<Tag[]>([])
  const [groups, setGroups] = useState<Group[]>([])

  // Menu state
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null)

  // Filter state
  const [filtersExpanded, setFiltersExpanded] = useState(false)

  // Delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [transactionToDelete, setTransactionToDelete] = useState<Transaction | null>(null)

  useEffect(() => {
  }, [formData])

  // Update categoryId when categories are loaded and no category is selected
  useEffect(() => {
    if (categories.length > 0 && !formData.categoryId) {
      const defaultCategory = categories.find(cat => cat.type === formData.type || cat.type === 'both')
      if (defaultCategory) {
        setFormData(prev => ({ ...prev, categoryId: defaultCategory.id }))
      }
    }
  }, [categories, formData.type, formData.categoryId])

  useEffect(() => {
    // Only load data if user is authenticated
    if (user && localStorage.getItem('auth_token')) {
      loadTransactions()
      loadCategories()
      loadTags()
      loadGroups()
    }
  }, [user, page, rowsPerPage, searchQuery, selectedType, selectedCategory, selectedGroup, dateRange])

  const loadTransactions = async () => {
    try {
      setLoading(true)
      const filters: any = {}
      
      if (selectedType !== 'all') filters.type = selectedType
      if (selectedCategory) filters.categoryId = selectedCategory
      if (selectedGroup) filters.groupId = selectedGroup
      if (searchQuery) filters.search = searchQuery
      if (dateRange.start) filters.dateFrom = format(dateRange.start, 'yyyy-MM-dd')
      if (dateRange.end) filters.dateTo = format(dateRange.end, 'yyyy-MM-dd')

      const result = await transactionService.getTransactions(page + 1, rowsPerPage, filters)
      setTransactions(result.data || [])
      setTotalCount(result.pagination?.total || 0)
      setError(null)
    } catch (err) {
      setError('Failed to load transactions')
      setTransactions([]) // Ensure transactions is always an array
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const loadCategories = async () => {
    try {
      const result = await categoryService.getCategories()
      setCategories(result)
    } catch (err) {
      console.error('Failed to load categories:', err)
    }
  }

  const loadTags = async () => {
    try {
      const result = await tagService.getTags()
      setTags(result)
    } catch (err) {
      console.error('Failed to load tags:', err)
    }
  }

  const loadGroups = async () => {
    try {
      console.log('Loading groups - checking auth state:', {
        hasToken: !!localStorage.getItem('auth_token'),
        tokenPreview: localStorage.getItem('auth_token')?.substring(0, 30) + '...'
      })
      
      const result = await groupService.getGroups()
      console.log('Groups loaded successfully:', result)
      setGroups(result)
    } catch (err) {
      console.error('Failed to load groups:', err)
      console.error('Groups API error details:', {
        error: err,
        hasToken: !!localStorage.getItem('auth_token'),
        tokenPreview: localStorage.getItem('auth_token')?.substring(0, 30) + '...'
      })
      setGroups([]) // Ensure groups is always an array
    }
  }

  const handleGroupChange = (event: any) => {
    const newValue = event.target.value
    const groupId = newValue ? Number(newValue) : undefined
    
    console.log('Group onChange triggered:', {
      rawValue: newValue,
      parsedValue: groupId,
      currentFormData: formData
    })
    
    setSelectedGroupId(groupId)
    setFormData(prevFormData => {
      const newFormData = { 
        ...prevFormData, 
        groupId: groupId
      }
      console.log('Setting new formData with callback:', newFormData)
      return newFormData
    })
  }

  const handleCreateTransaction = async () => {
    try {
      // Validate required fields
      if (!formData.categoryId) {
        alert('Please select a category')
        return
      }
      
      if (!formData.transactionDate) {
        alert('Please select a transaction date')
        return
      }
      
      if (!formData.description.trim()) {
        alert('Please enter a description')
        return
      }
      
      if (formData.amount <= 0) {
        alert('Please enter a valid amount')
        return
      }

      // Prepare the data for submission
      const transactionData = {
        ...formData,
        categoryId: formData.categoryId, // Ensure it's set
        transactionDate: formData.transactionDate // Ensure it's set
      }

      console.log('Creating transaction with data:', transactionData)
      await transactionService.createTransaction(transactionData)
      setFormOpen(false)
      resetForm()
      loadTransactions()
    } catch (err) {
      console.error('Failed to create transaction:', err)
      alert('Failed to create transaction. Please try again.')
    }
  }

  const handleUpdateTransaction = async () => {
    if (!editingTransaction) return
    
    try {
      await transactionService.updateTransaction(editingTransaction.id, formData)
      setFormOpen(false)
      setEditingTransaction(null)
      resetForm()
      loadTransactions()
    } catch (err) {
      console.error('Failed to update transaction:', err)
    }
  }

  const handleDeleteTransaction = async () => {
    if (!transactionToDelete) return

    try {
      await transactionService.deleteTransaction(transactionToDelete.id)
      setDeleteConfirmOpen(false)
      setTransactionToDelete(null)
      loadTransactions()
    } catch (err) {
      console.error('Failed to delete transaction:', err)
    }
  }

  const resetForm = () => {
    setSelectedGroupId(undefined)
    setFormData({
      amount: 0,
      description: '',
      type: 'expense',
      categoryId: categories.length > 0 ? categories[0].id : undefined, // Set to first available category
      tagIds: [],
      transactionDate: format(new Date(), 'yyyy-MM-dd'),
    })
  }

  const openEditForm = (transaction: Transaction) => {
    setEditingTransaction(transaction)
    setSelectedGroupId(transaction.groupId)
    setFormData({
      amount: transaction.amount,
      description: transaction.description,
      type: transaction.type,
      categoryId: transaction.categoryId,
      tagIds: transaction.tags?.map(tag => tag.id) || [],
      transactionDate: transaction.transactionDate,
      groupId: transaction.groupId,
    })
    setFormOpen(true)
    setMenuAnchor(null)
  }

  const openDeleteConfirm = (transaction: Transaction) => {
    setTransactionToDelete(transaction)
    setDeleteConfirmOpen(true)
    setMenuAnchor(null)
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount)
  }

  const getTypeColor = (type: string) => {
    return type === 'income' ? 'success' : 'error'
  }

  const getTypeIcon = (type: string) => {
    return type === 'income' ? <TrendingUp /> : <TrendingDown />
  }

  if (loading && transactions.length === 0) {
    return <LoadingSpinner message="Loading transactions..." />
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 0 }
      }}>
        <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
          Transactions
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            // Ensure a category is selected when opening the form
            if (categories.length > 0 && !formData.categoryId) {
              const defaultCategory = categories.find(cat => cat.type === 'expense' || cat.type === 'both')
              if (defaultCategory) {
                setFormData(prev => ({ ...prev, categoryId: defaultCategory.id }))
              }
            }
            setFormOpen(true)
          }}
          sx={{ display: { xs: 'none', sm: 'flex' } }}
        >
          Add Transaction
        </Button>
      </Box>

      {error && (
        <ErrorMessage
          message={error}
          onRetry={loadTransactions}
        />
      )}

      {/* Filters */}
      <Paper sx={{ mb: 3 }}>
        {/* Mobile Filter Toggle */}
        {isMobile && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              cursor: 'pointer',
            }}
            onClick={() => setFiltersExpanded(!filtersExpanded)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterList />
              <Typography variant="body1" fontWeight={500}>
                Filters
              </Typography>
            </Box>
            {filtersExpanded ? <ExpandLess /> : <ExpandMore />}
          </Box>
        )}

        {/* Filter Content */}
        <Collapse in={!isMobile || filtersExpanded}>
          <Box sx={{ p: 2, pt: isMobile ? 0 : 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  placeholder="Search transactions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value as any)}
                    label="Type"
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="income">Income</MenuItem>
                    <MenuItem value="expense">Expense</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={selectedCategory || ''}
                    onChange={(e) => setSelectedCategory(e.target.value ? Number(e.target.value) : null)}
                    label="Category"
                  >
                    <MenuItem value="">All Categories</MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Group</InputLabel>
                  <Select
                    value={selectedGroup || ''}
                    onChange={(e) => setSelectedGroup(e.target.value ? Number(e.target.value) : null)}
                    label="Group"
                  >
                    <MenuItem value="">Personal</MenuItem>
                    {(groups || []).map((group) => (
                      <MenuItem key={group.id} value={group.id}>
                        {group.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <DateRangePicker
                  startDate={dateRange.start}
                  endDate={dateRange.end}
                  onChange={(start, end) => setDateRange({ start, end })}
                />
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Paper>

      {/* Transactions Content */}
      {isMobile ? (
        // Mobile Card View
        <Box>
          <TransactionList
            transactions={transactions}
            onMenuClick={(event, transaction) => {
              setSelectedTransaction(transaction)
              setMenuAnchor(event.currentTarget)
            }}
          />
          {/* Mobile Pagination */}
          {totalCount > 0 && (
            <Paper sx={{ mt: 2 }}>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10))
                  setPage(0)
                }}
                labelRowsPerPage="Items per page:"
                rowsPerPageOptions={[10, 25, 50]}
              />
            </Paper>
          )}
        </Box>
      ) : (
        // Desktop Table View
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Tags</TableCell>
                  <TableCell>Group</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(transactions || []).map((transaction) => {
                  // Safe date formatting function
                  const formatSafeDate = (dateValue: string | null | undefined): string => {
                    try {
                      if (!dateValue || dateValue === null || dateValue === undefined) {
                        console.warn('Missing date value')
                        return 'No Date'
                      }
                      
                      const date = new Date(dateValue)
                      if (isNaN(date.getTime())) {
                        console.warn('Invalid date value:', dateValue)
                        return 'Invalid Date'
                      }
                      return format(date, 'MMM dd, yyyy')
                    } catch (error) {
                      console.error('Date formatting error:', error, 'for value:', dateValue)
                      return 'Invalid Date'
                    }
                  }

                  return (
                  <TableRow key={transaction.id}>
                    <TableCell>
                      {formatSafeDate(transaction.transactionDate)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getTypeIcon(transaction.type)}
                        {transaction.description}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={transaction.category?.name || 'Uncategorized'}
                        size="small"
                        sx={{ backgroundColor: transaction.category?.color || '#gray', color: 'white' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {transaction.tags?.map((tag) => (
                          <Chip
                            key={tag.id}
                            label={tag.name}
                            size="small"
                            variant="outlined"
                            sx={{ borderColor: tag.color, color: tag.color }}
                          />
                        )) || []}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {transaction.group?.name || 'Personal'}
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={formatCurrency(transaction.amount)}
                        color={getTypeColor(transaction.type)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        onClick={(e) => {
                          setSelectedTransaction(transaction)
                          setMenuAnchor(e.currentTarget)
                        }}
                      >
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10))
              setPage(0)
            }}
          />
        </Paper>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={() => selectedTransaction && openEditForm(selectedTransaction)}>
          <Edit sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={() => selectedTransaction && openDeleteConfirm(selectedTransaction)}>
          <Delete sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Transaction Form Dialog */}
      <Dialog 
        key={`transaction-dialog-${formOpen ? 'open' : 'closed'}-${editingTransaction?.id || 'new'}`}
        open={formOpen} 
        onClose={() => setFormOpen(false)} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 3 },
            m: { xs: 0, sm: 2 },
          }
        }}
      >
        <DialogTitle>
          {editingTransaction ? 'Edit Transaction' : 'Add Transaction'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: Number(e.target.value) })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                  label="Type"
                >
                  <MenuItem value="income">Income</MenuItem>
                  <MenuItem value="expense">Expense</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.categoryId || ''}
                  onChange={(e) => {
                    const categoryId = e.target.value ? Number(e.target.value) : undefined
                    setFormData({ ...formData, categoryId })
                  }}
                  label="Category"
                  required
                >
                  {!formData.categoryId && (
                    <MenuItem value="" disabled>
                      Select Category *
                    </MenuItem>
                  )}
                  {categories
                    .filter(cat => cat.type === formData.type || cat.type === 'both')
                    .map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={formData.transactionDate}
                onChange={(e) => setFormData({ ...formData, transactionDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                multiple
                options={tags}
                getOptionLabel={(option) => option.name}
                value={tags.filter(tag => formData.tagIds.includes(tag.id))}
                onChange={(_, newValue) => {
                  setFormData({ ...formData, tagIds: newValue.map(tag => tag.id) })
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Tags" placeholder="Select tags" />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Group</InputLabel>
                <Select
                  key={`group-select-${selectedGroupId || 'none'}`}
                  value={selectedGroupId ? selectedGroupId.toString() : ''}
                  onChange={handleGroupChange}
                  label="Group"
                >
                  <MenuItem value="">Personal</MenuItem>
                  {(groups || []).map((group) => {
                    console.log('Rendering group option:', group)
                    return (
                      <MenuItem key={group.id} value={group.id.toString()}>
                        {group.name}
                      </MenuItem>
                    )
                  })}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button
            onClick={editingTransaction ? handleUpdateTransaction : handleCreateTransaction}
            variant="contained"
          >
            {editingTransaction ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Delete Transaction"
        message={`Are you sure you want to delete "${transactionToDelete?.description}"?`}
        onConfirm={handleDeleteTransaction}
        onCancel={() => setDeleteConfirmOpen(false)}
        severity="error"
      />

      {/* Floating Action Button for mobile */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{
          position: 'fixed',
          bottom: { xs: 80, sm: 16 }, // Account for mobile bottom navigation
          right: 16,
          display: { xs: 'flex', sm: 'none' },
          zIndex: 1000,
        }}
        onClick={() => {
          // Ensure a category is selected when opening the form
          if (categories.length > 0 && !formData.categoryId) {
            const defaultCategory = categories.find(cat => cat.type === 'expense' || cat.type === 'both')
            if (defaultCategory) {
              setFormData(prev => ({ ...prev, categoryId: defaultCategory.id }))
            }
          }
          setFormOpen(true)
        }}
      >
        <Add />
      </Fab>
    </Box>
  )
}

export default TransactionsPage